/**
 * Report Builder — 4-column layout.
 *
 *   1) Sections     — list, search, note-count badge, collapse
 *   2) Notes & Snippets — mapped snippets, filters, choices (col-2)
 *   3) Draft Report — docx editor (dominant column)
 *   4) Report QA    — AI quality panel (toggled from the top bar)
 *
 * State + the docx editor ref live in <ReportBuilderEditorProvider/>
 * (mounted in Layout). This page activates the provider and wires the
 * columns together; the editor (col-3) is the only flexible track.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { GripVertical } from "lucide-react";
import { ThreeDots } from "react-loader-spinner";
import { useDispatch } from "react-redux";

import { FeatureAccessNotice } from "@components/FeatureAccessNotice";
import { ReportQAPanel } from "@components/ReportQAPanel";

import { CAPABILITIES } from "@constants/capabilities";
import { CLIENT_KEYS } from "@constants/clientKeys";

import { useReorderModuleExtracts } from "@hooks/mutations/use-module-extracts";
import { useFinaliseReport } from "@hooks/mutations/use-report-finalisation";
import { useDownloadResponsePdf } from "@hooks/mutations/use-templates";
import { useCaseProcessingStatus } from "@hooks/queries/use-case-processing-status";
import { useFinalisationState } from "@hooks/queries/use-finalisation-state";
import { useCapabilities, useIsAdmin } from "@hooks/queries/use-me";

import {
  ASSESSMENT_SECTIONS,
  resolveSectionId,
} from "@pages/ReportStudio/AudioTranscriber/constants/sections";
import { setPendingHighlightText } from "@pages/ReportStudio/AudioTranscriber/utils/pendingHighlightHandoff";
import { useCaseDetail } from "@pages/ReportStudio/CaseDetail/context";

import { setMessage } from "@redux/notification";

import { getAccessTokenCookie } from "@utils/cookies";
import { getEnv } from "@utils/env";
import { getFriendlyErrorMessage } from "@utils/notification";
import { cn } from "@utils/ui";

import { AdminFinalisationControls } from "./components/AdminFinalisationControls";
import { CodeOfConductPanel } from "./components/CodeOfConductPanel";
import { DownloadPdfModal } from "./components/DownloadPdfModal";
import { DraftReportPreviewModal } from "./components/DraftReportPreviewModal";
import { FinaliseReportModal } from "./components/FinaliseReportModal";
import { HeaderActions } from "./components/HeaderActions";
import { LeaveWithoutSavingModal } from "./components/LeaveWithoutSavingModal";
import { ModulesPanel } from "./components/ModulesPanel";
import { NotesPanel } from "./components/NotesPanel";
import { ReportSectionEditor } from "./editor/ReportSectionEditor";
import { clampNotesWidth, notesMaxWidth } from "./editor/autosave";
import { useReportBuilderEditor } from "./editor/context";
import type { ReportNote } from "./editor/notes";
import { saveFailureMessage } from "./editor/persist-docx";
import { useLeaveNavigationGuard } from "./editor/useLeaveNavigationGuard";
import { isCodeOfConductSection } from "./editor/utils";
import {
  PreFinaliseSaveFailedError,
  useConfirmFinalise,
} from "./hooks/use-confirm-finalise";

// slug (AssessmentColumnNameEnum) → human label for scribe-note provenance.
// ASSESSMENT_SECTIONS has multiple entries per key (service-type variants); keep
// the first occurrence per key so legacy section titles are used as labels.
const FIELD_LABELS: Record<string, string> = (() => {
  const map = new Map<string, string>();
  ASSESSMENT_SECTIONS.forEach((s) => {
    if (s.key && !map.has(s.key)) map.set(s.key, s.title);
  });
  return Object.fromEntries(map);
})();
const fieldLabel = (slug: string) => FIELD_LABELS[slug] ?? "Scribe";

const ReportBuilderPage = () => {
  const { templateId, id: caseNumber } = useParams({ strict: false }) as {
    templateId: string;
    id: string;
  };
  const { setRightContent } = useCaseDetail();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    activate,
    isLoading,
    sectionRows,
    activeSection,
    showAll,
    setShowAll,
    activeChoices,
    handleSelectSection,
    choiceByModule,
    handleChoiceChange,
    activeNotes,
    allNotes,
    handleSave,
    handleDownload,
    isDownloading,
    draftId,
    caseData,
    template,
  } = useReportBuilderEditor();

  // MDRS-555 / audit RB-9: ask before an in-app route change drops unsaved
  // work. `beforeunload` (wired in the provider) never fires for client-side
  // navigation, which is how this page is usually left. Mounted here rather
  // than in the provider so it is active only while a report is actually open.
  const leaveGuard = useLeaveNavigationGuard();

  const downloadPdf = useDownloadResponsePdf();
  const reorderNotes = useReorderModuleExtracts();
  const reorderMutateAsync = reorderNotes.mutateAsync;
  const handleReorderNotes = useCallback(
    async (orderedIds: string[]) => {
      if (!caseNumber || !templateId) return;
      try {
        // Rejects on failure so NotesPanel can roll back its optimistic order.
        await reorderMutateAsync({ caseNumber, templateId, orderedIds });
      } catch (error) {
        dispatch(
          setMessage({
            type: "error",
            content:
              error instanceof Error ? error.message : "Failed to save note order.",
          })
        );
        throw error;
      }
    },
    [reorderMutateAsync, caseNumber, templateId, dispatch]
  );

  // SCRIBE-07 back-link: navigate a Scribe-sourced note to its original
  // position. category_slug alone isn't enough to know which Scribe tab id
  // to land on — the same slug maps to a DIFFERENT id depending on the
  // case's service_type (see resolveSectionId's doc comment), so this must
  // be resolved against the CURRENT case, not just looked up statically.
  //
  // note.text (clinical content) travels via setPendingHighlightText, NOT
  // the URL — only the non-sensitive section id is a search param.
  const handleLocateNote = useCallback(
    (note: ReportNote) => {
      if (!caseNumber || !note.categorySlug) return;
      const sectionId = resolveSectionId(caseData?.service_type, note.categorySlug);
      if (!sectionId) {
        dispatch(
          setMessage({
            type: "info",
            content:
              "This note's original section is no longer part of this case's assessment type.",
          })
        );
        return;
      }
      setPendingHighlightText(note.text);
      navigate({
        to: `/report-studio/${caseNumber}/assessment-scribe`,
        search: { section: sectionId },
      });
    },
    [caseNumber, caseData?.service_type, dispatch, navigate]
  );
  // `downloadPdf` is a fresh object on every render (React Query observer
  // result), but `.mutateAsync` is bound once in the observer constructor and
  // is stable. Pin the stable ref so `handleConfirmDownloadPdf`'s useCallback
  // doesn't recompute on every render — otherwise the layout-wiring useEffect
  // below re-runs each render and its cleanup (which clears the case-detail
  // header content) trips React's max-update-depth guard.
  const downloadPdfMutateAsync = downloadPdf.mutateAsync;
  const downloadPdfIsPending = downloadPdf.isPending;

  const [pdfModalOpen, setPdfModalOpen] = useState(false);

  // Preview modal — render the SAME PDF the Download button produces.
  // The DocxEditor's in-editor render can't paint footers (a
  // documented limitation of the vendored eigenpal editor), so the
  // only way to make Preview match the downloaded artifact is to
  // serve the actual PDF. Trade-off: a few seconds while LibreOffice
  // does the .docx → PDF conversion on each preview open.
  const queryClient = useQueryClient();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | undefined>(undefined);
  // Clean up the blob URL after the modal closes. Tracked in a ref
  // (not effect cleanup) because `setPreviewPdfUrl(undefined)` below
  // re-runs this effect immediately, which would clear the timeout
  // before the revoke fires. The ref-based version survives the
  // re-render and only resets if a NEW close lands before the timer.
  const revokeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!previewOpen && previewPdfUrl) {
      const stale = previewPdfUrl;
      if (revokeTimerRef.current) clearTimeout(revokeTimerRef.current);
      // Defer one tick so the iframe finishes unloading before we
      // pull the URL out from under it.
      revokeTimerRef.current = setTimeout(() => {
        URL.revokeObjectURL(stale);
        revokeTimerRef.current = null;
      }, 500);
      setPreviewPdfUrl(undefined);
    }
  }, [previewOpen, previewPdfUrl]);
  const handleOpenPreview = useCallback(async () => {
    // Open the modal IMMEDIATELY with the skeleton, then async-fetch
    // the PDF. The conversion can take several seconds; a synchronous
    // wait would leave the toolbar button looking unresponsive.
    setPreviewPdfUrl(undefined);
    setPreviewOpen(true);
    try {
      // Force-save so the backend re-merges the latest editor state
      // into the saved blob (which download-pdf converts).
      //
      // MDRS-545: the result is deliberately NOT gated on. Preview is
      // read-only — a failed save means you see the previously saved version,
      // which beats showing nothing, and handleSave has already raised its own
      // error toast. Contrast finalise, which freezes and locks and therefore
      // must stop (see useConfirmFinalise).
      await handleSave();
    } catch {
      // Same reasoning: a throw here still falls through to the preview.
    }
    // handleSave invalidates draft-status; refetch and read fresh
    // data so we have an up-to-date draft response id.
    let draftResponseId: string | undefined;
    if (templateId && caseNumber) {
      const queryKey = [
        CLIENT_KEYS.REPORT_GENERATOR,
        CLIENT_KEYS.TEMPLATES,
        templateId,
        CLIENT_KEYS.CASES,
        CLIENT_KEYS.DRAFT_STATUS,
        caseNumber,
      ];
      try {
        await queryClient.refetchQueries({ queryKey, exact: true });
        const fresh = queryClient.getQueryData<{ draft_id?: string | null }>(
          queryKey
        );
        draftResponseId = fresh?.draft_id ?? draftId ?? undefined;
      } catch {
        draftResponseId = draftId ?? undefined;
      }
    } else {
      draftResponseId = draftId ?? undefined;
    }
    if (!draftResponseId) {
      dispatch(
        setMessage({
          type: "error",
          content: "Save the draft before previewing.",
        })
      );
      setPreviewOpen(false);
      return;
    }
    // Owner-or-admin preview endpoint -- same render path as Download PDF,
    // but not admin-gated and not audit-logged as a download (MDRS-271 locked
    // download-pdf to admins; doctors still need to look before Finalise).
    // Strip leading slash off the base URL to avoid `//` in the path.
    const baseUrl = getEnv("VITE_BACKEND_URL") || "http://localhost:8000";
    const url = `${baseUrl.replace(/\/$/, "")}/api/v1/report-generator/responses/${encodeURIComponent(
      draftResponseId
    )}/preview-pdf`;
    const token = getAccessTokenCookie();
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        throw new Error(`PDF preview failed: ${res.status}`);
      }
      const blob = await res.blob();
      const pdfBlob =
        blob.type === "application/pdf"
          ? blob
          : new Blob([blob], { type: "application/pdf" });
      setPreviewPdfUrl(URL.createObjectURL(pdfBlob));
    } catch (err) {
      console.warn("Failed to fetch preview PDF:", err);
      dispatch(
        setMessage({
          type: "error",
          content:
            err instanceof Error ? err.message : "Failed to generate preview PDF.",
        })
      );
      setPreviewOpen(false);
    }
  }, [handleSave, queryClient, templateId, caseNumber, draftId, dispatch]);

  const handleConfirmDownloadPdf = useCallback(
    async (password?: string) => {
      const stem = caseData?.examinee_name?.replace(/\s+/g, "-") ?? caseNumber;
      let stale = false;
      let staleCause = "";
      try {
        // Save first so the current editor .docx is uploaded (draft_docx_url),
        // which is what the PDF endpoint converts. Without this a fresh draft
        // has nothing to export.
        //
        // MDRS-545: deliberately not gated on the result. The download is
        // read-only and reversible — you can re-download after fixing the
        // save — so proceeding beats refusing. But now that handleSave reports
        // success, say so rather than letting a save-error toast and a file
        // arriving look unrelated. Worth revisiting if downloads ever become
        // an outbound hand-off rather than an admin convenience.
        const outcome = await handleSave();
        if (!outcome.ok) staleCause = saveFailureMessage(outcome.reason);
        stale = !outcome.ok;
      } catch (error) {
        // A throw still falls through to the download — and it is the WORSE
        // save outcome, so it must not be the one that goes unexplained. The
        // save's own toast does NOT survive (single message slot), so map the
        // error and carry the cause, same as the finalise gate.
        stale = true;
        staleCause = getFriendlyErrorMessage(error);
      }
      if (stale) {
        // Carries the cause where we know it, for the same reason the finalise
        // gate does: one message slot, so consequence-only would destroy it.
        dispatch(
          setMessage({
            type: "warning",
            content: [
              staleCause,
              "This PDF may not include your most recent changes.",
            ]
              .filter(Boolean)
              .join(" "),
          })
        );
      }
      if (!draftId) {
        const msg = "Save the draft before exporting to PDF.";
        dispatch(setMessage({ type: "error", content: msg }));
        throw new Error(msg);
      }
      try {
        await downloadPdfMutateAsync({
          responseId: draftId,
          fileName: `${stem}-${template?.name ?? "report"}.pdf`,
          password,
        });
      } catch (error) {
        dispatch(
          setMessage({
            type: "error",
            content:
              error instanceof Error
                ? error.message
                : "Failed to download PDF report.",
          })
        );
        throw error;
      }
    },
    [
      handleSave,
      downloadPdfMutateAsync,
      draftId,
      caseData,
      caseNumber,
      template,
      dispatch,
    ]
  );

  const handleDownloadPdf = useCallback(() => {
    setPdfModalOpen(true);
  }, []);

  // --- Finalise Report (MDRS-271) ------------------------------------------
  // Doctors finalise instead of downloading: this locks the whole case and
  // submits the report to MEDirect for internal Vinci upload. Admins keep the
  // download path (see HeaderActions). `finalised` is session-local — the lock
  // itself is enforced server-side (writes 409 once finalised), so a reload that
  // loses this flag still can't edit; it just won't show the "Finalised" pill.
  const isAdmin = useIsAdmin();
  // MDRS-336: the .docx caret on the doctor's Finalise CTA mirrors the server
  // gate rather than a role check, so the access-configuration toggle governs
  // both. Admins always get their own download split button regardless.
  const { can, isLoading: isCapabilitiesLoading } = useCapabilities();
  const canDownloadDocx = can("report:download_docx");
  // MDRS-443: the Report QA button mirrors the server gate (report_qa:use)
  // rather than always being offered.
  const canUseReportQA = can(CAPABILITIES.REPORT_QA);
  const finalise = useFinaliseReport();
  const finaliseMutateAsync = finalise.mutateAsync;
  const finaliseIsPending = finalise.isPending;
  const [finaliseModalOpen, setFinaliseModalOpen] = useState(false);
  const [finalisedLocal, setFinalisedLocal] = useState(false);
  // Persist the finalised/locked state across reloads by reading it back from
  // the server, OR-ed with the just-finalised flag from this session.
  const finalisationState = useFinalisationState(draftId, { enabled: !!draftId });
  const finalised = finalisedLocal || !!finalisationState.data?.finalised_at;
  // MDRS-508: hold Finalise back while the case's documents are still arriving
  // (same query as the case-detail banner, so React Query shares the poll).
  const caseProcessing = useCaseProcessingStatus({ case_number: caseNumber });
  const isCaseProcessing = !!caseProcessing.data?.is_processing;
  // Before any answer, a failed status request means "unknown", not idle — so
  // Finalise is held back too; the case-page notice offers a retry. (Later
  // failed polls keep the last good answer.)
  const isProcessingStatusUnavailable =
    caseProcessing.isError && !caseProcessing.data;
  const refetchCaseProcessing = caseProcessing.refetch;

  const handleFinalise = useCallback(() => {
    setFinaliseModalOpen(true);
  }, []);

  // MDRS-545: the save gate lives in the hook so it can be tested without
  // rendering the editor. Finalise must not freeze or lock anything unless
  // the save fully landed — a .docx upload failure resolves rather than
  // throwing, so try/catch alone never caught it.
  const onFinalised = useCallback(() => setFinalisedLocal(true), []);
  const confirmFinalise = useConfirmFinalise({
    handleSave,
    finaliseMutateAsync,
    dispatch,
    draftId,
    onFinalised,
  });
  // MDRS-508: a refused finalise may mean an upload started after our last
  // poll — refresh the processing status so the banner and disabled state
  // catch up. (Not when the pre-save failed: the server was never asked.)
  const handleConfirmFinalise = useCallback(
    async (attestationVersion: string) => {
      try {
        await confirmFinalise(attestationVersion);
      } catch (error) {
        if (!(error instanceof PreFinaliseSaveFailedError)) {
          void refetchCaseProcessing();
        }
        throw error;
      }
    },
    [confirmFinalise, refetchCaseProcessing]
  );

  useEffect(() => {
    if (!caseNumber || !templateId) return;
    activate(caseNumber, templateId);
  }, [activate, caseNumber, templateId]);

  // Column visibility toggles. Editor (col-3) is always present and is the
  // only flexible track — it takes most of the width.
  const [notesHidden, setNotesHidden] = useState(false);
  const [qaHidden, setQaHidden] = useState(true);
  const [sectionsCollapsed, setSectionsCollapsed] = useState(false);

  // MDRS-139: Code of Conduct is a browse-and-copy section. When it's the
  // active section the middle slot renders the CoC panel (jurisdiction
  // dropdown + copy) instead of Notes & Snippets — CoC has no mapped notes and
  // isn't authored inline. Purely derived from the section selection.
  const cocActive = isCodeOfConductSection(activeSection);

  const handleSelectModule = useCallback(
    (key: string) => {
      // Selecting CoC closes QA (the right rail) so the middle slot is the
      // focus; the notes panel is unhidden for every other section.
      if (isCodeOfConductSection(key)) {
        setQaHidden(true);
        handleSelectSection(key);
        return;
      }
      setNotesHidden(false);
      handleSelectSection(key);
    },
    [handleSelectSection]
  );

  // Notes & Snippets is drag-resizable (mirrors Assessment Prep's
  // ResizablePanels): a pointer handle on its left edge writes `notesWidth`,
  // which feeds the connected-card slot AND the sidebar grid track. Min 240px;
  // max is half the main container, measured live per drag (see notesMaxWidth).
  // Sections track is fixed (120 collapsed / 260).
  const NOTES_MIN = 240;
  const [notesWidth, setNotesWidth] = useState(288);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleNotesResize = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      const startX = e.clientX;
      const startWidth = notesWidth;
      // Cap the Notes panel at half the main container. Measured once on
      // pointer-down to avoid repeated reflows during the drag.
      const containerWidth =
        containerRef.current?.getBoundingClientRect().width ?? 0;
      const max = notesMaxWidth(containerWidth, NOTES_MIN);
      const onMove = (ev: PointerEvent) => {
        // Handle sits on the Notes panel's RIGHT edge → dragging right widens.
        const next = startWidth + (ev.clientX - startX);
        setNotesWidth(clampNotesWidth(next, NOTES_MIN, max));
      };
      const onUp = () => {
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    },
    [notesWidth]
  );

  // Editor is the dominant 1fr track. Sections + Notes share one connected
  // card; editor + QA are separate cards with a gap. Sidebar track width =
  // sections (120 collapsed / 260) + notes (notesWidth if shown). Driven via
  // inline gridTemplateColumns so the notes width can be dynamic (Tailwind
  // can't JIT interpolated arbitrary values).
  // Report QA occupies the single right rail. (Code of Conduct is a section,
  // not a right-rail panel — see cocActive / handleSelectModule.)
  const handleToggleQA = useCallback(() => {
    setQaHidden((v) => !v);
  }, []);

  const sectionsW = sectionsCollapsed ? 120 : 260;
  // The middle slot (notesWidth) is shown when Notes is visible OR when the CoC
  // panel is active (CoC renders there regardless of the notes-hidden toggle).
  const middleSlotShown = cocActive || !notesHidden;
  const sidebarW = sectionsW + (middleSlotShown ? notesWidth : 0);
  const gridTemplateColumns = !qaHidden
    ? `${sidebarW}px 1fr 320px`
    : `${sidebarW}px 1fr`;

  // MDRS-443: gated on the same capability as the body below — otherwise a
  // denied user still sees (and can click) the Preview/Finalise/Download
  // actions in the header even though the content area refuses them.
  useEffect(() => {
    if (isCapabilitiesLoading || !can(CAPABILITIES.REPORT_BUILDER)) {
      setRightContent(null);
      return;
    }

    setRightContent(
      <HeaderActions
        onPreview={handleOpenPreview}
        onToggleQA={handleToggleQA}
        onDownloadDocx={handleDownload}
        onDownloadPdf={handleDownloadPdf}
        onFinalise={handleFinalise}
        isDownloading={isDownloading}
        isDownloadingPdf={downloadPdfIsPending}
        isFinalising={finaliseIsPending}
        canDownloadPdf={!!draftId}
        finalised={finalised}
        isAdmin={isAdmin}
        canDownloadDocx={canDownloadDocx}
        canUseReportQA={canUseReportQA}
        isCaseProcessing={isCaseProcessing}
        isProcessingStatusUnavailable={isProcessingStatusUnavailable}
      />
    );
    return () => {
      setRightContent(null);
    };
  }, [
    setRightContent,
    handleOpenPreview,
    handleToggleQA,
    handleDownload,
    handleDownloadPdf,
    handleFinalise,
    isDownloading,
    downloadPdfIsPending,
    finaliseIsPending,
    draftId,
    finalised,
    isAdmin,
    canDownloadDocx,
    canUseReportQA,
    isCaseProcessing,
    isProcessingStatusUnavailable,
    can,
    isCapabilitiesLoading,
  ]);

  // MDRS-443: the case-detail tab bar only hides this tab's *trigger* when
  // the capability is off — it never blocks the routed content itself, so a
  // tab already open when the capability is revoked, or a direct URL nav
  // straight to this route, both reached the page regardless. Re-checked
  // here (this page has no route-level wrapper the way Assessment Scribe
  // does, so the guard lives in the page itself, matching Assessment Prep).
  if (isCapabilitiesLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <ThreeDots />
      </div>
    );
  }

  if (!can(CAPABILITIES.REPORT_BUILDER)) {
    return (
      <FeatureAccessNotice
        title="You don't have access to Report Builder."
        body="Ask an admin to enable this feature for your account."
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      {/* Outside the editor grid below: it portals when open and renders null
          otherwise, so it must not sit between grid columns. */}
      <LeaveWithoutSavingModal
        open={leaveGuard.blocked}
        onLeave={leaveGuard.leave}
        onStay={leaveGuard.stay}
      />
      {/* Admin-only finalisation controls (MDRS-271): download frozen PDF, mark
          uploaded to Vinci, break-glass unlock. Hidden for doctors. */}
      {isAdmin && draftId && (
        <AdminFinalisationControls
          responseId={draftId}
          caseNoticeShown={isCaseProcessing || isProcessingStatusUnavailable}
        />
      )}

      <div
        ref={containerRef}
        className="grid min-h-0 flex-1 gap-2"
        style={{ gridTemplateColumns }}
      >
        {/* Sections + Notes — one connected card (shared border, internal divider). */}
        <div className="flex min-h-0 overflow-hidden rounded-xl border bg-background-white">
          <div
            className={cn(
              "shrink-0 overflow-hidden transition-[width] duration-200 ease-out",
              sectionsCollapsed ? "w-[120px]" : "w-[256px]"
            )}
          >
            {/* key on collapsed → the swapped panel fades in on toggle */}
            <div
              key={sectionsCollapsed ? "collapsed" : "expanded"}
              className="h-full duration-200 animate-in fade-in"
            >
              <ModulesPanel
                rows={sectionRows}
                activeKey={activeSection}
                isLoading={isLoading}
                onSelect={handleSelectModule}
                collapsed={sectionsCollapsed}
                onToggleCollapse={() => setSectionsCollapsed((v) => !v)}
              />
            </div>
          </div>

          {/* MDRS-139: when Code of Conduct is the active section, the middle
            slot shows the CoC browse-and-copy panel (jurisdiction dropdown +
            copy) instead of Notes & Snippets — CoC carries no mapped notes. */}
          {cocActive ? (
            <div
              className="flex min-h-0 shrink-0 border-l"
              style={{ width: `${notesWidth}px` }}
            >
              <div className="min-w-0 flex-1 p-1.5">
                <CodeOfConductPanel />
              </div>
              {/* Drag handle on the right edge — resize the CoC panel (shares
                notesWidth with Notes & Snippets). */}
              <div
                data-testid="coc-resize-handle"
                onPointerDown={handleNotesResize}
                className="group relative flex w-4 shrink-0 cursor-col-resize select-none items-center justify-center border-l border-border transition-colors hover:border-primary/30 hover:bg-primary/5 active:bg-primary/10"
                title="Drag to resize"
              >
                <GripVertical className="text-muted-foreground/40 size-5 transition-colors group-hover:text-primary group-active:text-primary" />
              </div>
            </div>
          ) : notesHidden ? null : (
            <div
              className="flex min-h-0 shrink-0 border-l"
              style={{ width: `${notesWidth}px` }}
            >
              <div className="min-w-0 flex-1">
                <NotesPanel
                  activeNotes={activeNotes}
                  allNotes={allNotes}
                  activeSection={activeSection}
                  showAll={showAll}
                  onShowAllChange={setShowAll}
                  activeChoices={activeChoices}
                  choiceByModule={choiceByModule}
                  onChoiceChange={handleChoiceChange}
                  fieldLabel={fieldLabel}
                  onReorder={handleReorderNotes}
                  onCollapse={() => setNotesHidden(true)}
                  onLocateNote={handleLocateNote}
                />
              </div>
              {/* Drag handle on the right edge — resize Notes & Snippets. */}
              <div
                data-testid="notes-resize-handle"
                onPointerDown={handleNotesResize}
                className="group relative flex w-4 shrink-0 cursor-col-resize select-none items-center justify-center border-l border-border transition-colors hover:border-primary/30 hover:bg-primary/5 active:bg-primary/10"
                title="Drag to resize"
              >
                <GripVertical className="text-muted-foreground/40 size-5 transition-colors group-hover:text-primary group-active:text-primary" />
              </div>
            </div>
          )}
        </div>

        <div className="overflow-hidden rounded-xl border">
          <ReportSectionEditor variant="inline" finalised={finalised} />
        </div>

        {!qaHidden ? (
          <div className="overflow-hidden rounded-xl border bg-background-white">
            {caseNumber && templateId ? (
              <ReportQAPanel
                templateId={templateId}
                caseNumber={caseNumber}
                onClose={() => setQaHidden(true)}
              />
            ) : null}
          </div>
        ) : null}

        <DraftReportPreviewModal
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          pdfUrl={previewPdfUrl}
          examineeName={caseData?.examinee_name}
          claimNumber={caseData?.claim_number}
          templateName={template?.name}
        />

        <DownloadPdfModal
          open={pdfModalOpen}
          isDownloading={downloadPdfIsPending}
          onOpenChange={setPdfModalOpen}
          onConfirm={handleConfirmDownloadPdf}
        />

        <FinaliseReportModal
          open={finaliseModalOpen}
          isSubmitting={finaliseIsPending}
          onOpenChange={setFinaliseModalOpen}
          onConfirm={handleConfirmFinalise}
          isCaseProcessing={isCaseProcessing}
        />
      </div>
    </div>
  );
};

export default ReportBuilderPage;
